import React, { useEffect, useRef, useState } from "react";
import { cn, sanitizeFileName } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Upload, X } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";

interface RentTimelineChartProps {
  startMiete?: number;
  steigerung?: number; // Annual percentage increase (e.g., 0.03 for 3%)
  jahre?: number;
  /** Optional image URL for the right side illustration */
  illustrationUrl?: string;
  /** Whether edit mode is active (shows upload button) */
  isEditMode?: boolean;
  /** Callback when illustration changes */
  onIllustrationChange?: (url: string | null) => void;
  /** Callback when form values change (for persistence) */
  onFieldChange?: (fieldId: string, value: any) => void;
  className?: string;
}

export function RentTimelineChart({
  startMiete: initialMiete = 1000,
  steigerung: initialSteigerung = 0.03,
  jahre: initialJahre = 35,
  illustrationUrl,
  isEditMode = false,
  onIllustrationChange,
  onFieldChange,
  className,
}: RentTimelineChartProps) {
  const currentYear = new Date().getFullYear();
  
  // Local state for editable values - initialize from props
  const [miete, setMiete] = useState(initialMiete);
  const [steigerung, setSteigerung] = useState(initialSteigerung);
  const [jahre, setJahre] = useState(initialJahre);
  const [localImageUrl, setLocalImageUrl] = useState<string | null>(illustrationUrl || null);
  const [imageScale, setImageScale] = useState(100);
  const [isUploading, setIsUploading] = useState(false);
  
  // Sync from props when they change (e.g., on page load)
  useEffect(() => {
    setMiete(initialMiete);
  }, [initialMiete]);
  
  useEffect(() => {
    setSteigerung(initialSteigerung);
  }, [initialSteigerung]);
  
  useEffect(() => {
    setJahre(initialJahre);
  }, [initialJahre]);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Keep local image in sync with persisted prop
  useEffect(() => {
    setLocalImageUrl(illustrationUrl || null);
  }, [illustrationUrl]);

  // Kumulierte Miete (vereinfachtes Modell wie im Beispiel):
  // Jahr 1 = 12 * Miete
  // Jahr N (N>1) = 12 * Miete * (1 + Anpassung) * N
  const calculateCumulativeRent = React.useCallback(
    (years: number, monthlyRent: number, annualIncrease: number) => {
      if (years <= 0) return 0;
      if (years === 1) return Math.round(12 * monthlyRent);
      return Math.round(12 * monthlyRent * (1 + annualIncrease) * years);
    },
    []
  );

  const data = React.useMemo(() => {
    const result = [];

    // Generate data points every 5 years
    for (let year = 0; year <= jahre; year++) {
      const cumulativeCost = calculateCumulativeRent(year, miete, steigerung);

      if (year % 5 === 0 || year === jahre) {
        result.push({
          year: currentYear + year,
          label: String(currentYear + year),
          cumulative: Math.round(cumulativeCost),
          displayValue: `€ ${new Intl.NumberFormat("de-AT").format(Math.round(cumulativeCost))},-`,
        });
      }
    }

    return result;
  }, [miete, steigerung, jahre, currentYear, calculateCumulativeRent]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-AT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Calculate cumulative total WITH annual increase
  const cumulativeTotal = calculateCumulativeRent(jahre, miete, steigerung);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const safeName = sanitizeFileName(file.name);
      const filePath = `rent-illustrations/${Date.now()}-${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("beratung-documents")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("beratung-documents")
        .getPublicUrl(filePath);

      const publicUrl = urlData?.publicUrl;
      if (!publicUrl) throw new Error("Konnte keine öffentliche URL erzeugen");

      setLocalImageUrl(publicUrl);
      onIllustrationChange?.(publicUrl);
    } catch (err) {
      console.error("Illustration Upload fehlgeschlagen:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setLocalImageUrl(null);
    onIllustrationChange?.(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={cn("p-6 space-y-6", className)}>
      {/* Title */}
      <div className="text-left">
        <h2 className="text-2xl md:text-3xl font-bold">
          <span className="text-foreground">Wie viel Miete zahlen </span>
          <span className="text-primary">Sie in {jahre} Jahren?*</span>
        </h2>
        <p className="text-lg italic text-muted-foreground mt-1">
          Der planbare Weg zum Eigentum.
        </p>
      </div>

      {/* 12-column grid layout */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left side: Chart (8 columns) */}
        <div className="col-span-12 lg:col-span-8">
          <div className="border-2 border-primary rounded-lg p-4">
            {/* Chart subtitle with inline editable values */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2 flex-wrap">
              <span>*Bsp: anfänglich €</span>
              <Input
                type="text"
                inputMode="numeric"
                value={miete}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  const numVal = val === '' ? 0 : Number(val);
                  setMiete(numVal);
                  onFieldChange?.('miete', numVal);
                }}
                className="w-16 h-7 text-sm border-primary text-center"
              />
              <span>monatliche Miete,</span>
              <Input
                type="text"
                inputMode="numeric"
                value={Math.round(steigerung * 100)}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  const numVal = (val === '' ? 0 : Number(val)) / 100;
                  setSteigerung(numVal);
                  onFieldChange?.('miet_steigerung', numVal);
                }}
                className="w-10 h-7 text-sm border-primary text-center"
              />
              <span>%</span>
              <span>Anpassung jährlich</span>
            </div>

            {/* Bar Chart */}
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={data} 
                  margin={{ top: 30, right: 20, left: 10, bottom: 5 }}
                >
                  <XAxis 
                    dataKey="label" 
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} 
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis hide />
                  <Tooltip
                    formatter={(value: number) => [formatCurrency(value), 'Kumuliert']}
                    labelStyle={{ fontWeight: 'bold', color: 'hsl(var(--foreground))' }}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      color: 'hsl(var(--popover-foreground))',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                    itemStyle={{ color: 'hsl(var(--popover-foreground))' }}
                  />
                  <Bar 
                    dataKey="cumulative" 
                    radius={[4, 4, 0, 0]}
                    fill="hsl(var(--muted))"
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={index === data.length - 1 
                          ? 'hsl(var(--primary))' 
                          : 'hsl(var(--muted-foreground) / 0.3)'
                        }
                      />
                    ))}
                    <LabelList 
                      dataKey="displayValue" 
                      position="top" 
                      fontSize={11}
                      fill="hsl(var(--muted-foreground))"
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Dotted line separator */}
            <div className="border-t-2 border-dashed border-primary/30 my-4" />

            {/* Input fields row - Miete x 12 x Jahre = Gesamtmiete (cumulative with increase) */}
            <div className="flex items-end gap-3 flex-wrap">
            <div className="flex flex-col">
                <Label className="text-sm font-medium mb-1">monatliche Miete</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={miete}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    const numVal = val === '' ? 0 : Number(val);
                    setMiete(numVal);
                    onFieldChange?.('miete', numVal);
                  }}
                  className="w-20 h-10 text-base text-center"
                />
              </div>
              
              <span className="text-lg font-medium text-muted-foreground pb-2">x 12 x</span>
              
              <div className="flex flex-col">
                <Label className="text-sm font-medium mb-1">Jahre</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={jahre}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    const numVal = val === '' ? 1 : Math.max(1, Number(val));
                    setJahre(numVal);
                    onFieldChange?.('mietjahre', numVal);
                  }}
                  className="w-16 h-10 text-base text-center"
                />
              </div>
              
              <span className="text-lg font-medium text-muted-foreground pb-2">=</span>
              
              <div className="flex flex-col">
                <Label className="text-sm font-medium mb-1">Gesamtmiete</Label>
                <div className="h-10 px-3 flex items-center bg-muted rounded-md text-base font-semibold text-primary">
                  {formatCurrency(cumulativeTotal)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right side: Illustration Card (4 columns) */}
        <div className="col-span-12 lg:col-span-4">
          <div className="border-2 border-primary rounded-lg p-4 h-full flex flex-col items-center justify-center min-h-[300px]">
            {localImageUrl ? (
              <div className="relative w-full h-full flex flex-col items-center justify-center gap-2">
                <img 
                  src={localImageUrl} 
                  alt="Illustration" 
                  className="object-contain transition-transform"
                  style={{ 
                    maxWidth: `${imageScale}%`, 
                    maxHeight: `${Math.min(280, 280 * imageScale / 100)}px` 
                  }}
                />
                {isEditMode && (
                  <>
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-0 right-0 h-8 w-8"
                      onClick={handleRemoveImage}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <div className="w-full max-w-[180px] mt-2">
                      <Label className="text-xs text-muted-foreground mb-1 block">
                        Größe: {imageScale}%
                      </Label>
                    <Slider
                        value={[imageScale]}
                        onValueChange={([v]) => setImageScale(v)}
                        min={50}
                        max={300}
                        step={10}
                        className="w-full"
                      />
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-center p-4 space-y-4 w-full">
                {isEditMode ? (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      disabled={isUploading}
                      className="border-dashed border-2 h-32 w-full flex flex-col gap-2"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-8 w-8 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        {isUploading ? "Upload läuft..." : "Grafik hochladen"}
                      </span>
                    </Button>
                  </>
                ) : (
                  <div className="text-sm text-muted-foreground">
                    Keine Grafik hinterlegt.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}